/** Portfolio analytics with visual trend, funnel, traffic mix, and VSL ranking. */
import type { VslProject } from "../../../contracts/src/contracts";
import type { VslStore } from "../store";
import { el, append, icon, ICONS, fmtDate } from "../dom";
import {
  getProjectMetrics,
  fmtCompact,
  fmtNumber,
  fmtPct,
  fmtTrend,
} from "../project-metrics";

type Metrics = ReturnType<typeof getProjectMetrics>;
type AnalyticsRow = { project: VslProject; metrics: Metrics };
export type StatisticsPeriodDays = 7 | 14 | 30 | 90;

const PERIOD_STORAGE_KEY = "fullvsl:statistics:period-days";
const PERIOD_OPTIONS: ReadonlyArray<{ days: StatisticsPeriodDays; label: string }> = [
  { days: 7, label: "Últimos 7 dias" },
  { days: 14, label: "Últimos 14 dias" },
  { days: 30, label: "Últimos 30 dias" },
  { days: 90, label: "Últimos 90 dias" },
];

export function renderAnalyticsOverviewView(
  store: VslStore,
  navigate: (path: string) => void,
): HTMLElement {
  const wrapper = el("div", "fva-an-page");
  const projects = store.list();
  const periodDays = readPeriod();

  const header = el("div", "fva-page-header");
  const headerText = el("div");
  append(headerText, el("h2", null, "Estatísticas"));
  append(headerText, el("p", null, "Desempenho de todas as suas cartas de vendas em vídeo."));
  const status = el("span", store.live ? "fva-data-badge is-live" : "fva-data-badge", store.live ? "Dados reais" : "Dados de demonstração");
  const periodControl = renderPeriodControl(periodDays);
  const headerActions = el("div", "fva-an-header-actions");
  append(headerActions, periodControl, status);
  append(header, headerText, headerActions);
  append(wrapper, header);

  if (projects.length === 0) {
    append(wrapper, renderEmpty(navigate));
    return wrapper;
  }

  const rows: AnalyticsRow[] = projects.map((project) => ({
    project,
    metrics: scaleMetricsForPeriod(
      getProjectMetrics(project.id, project.name, project.status),
      periodDays,
    ),
  }));
  const published = rows.filter((row) => row.project.status === "published");
  const totalImpressions = rows.reduce((sum, row) => sum + row.metrics.impressions, 0);
  const totalPlays = rows.reduce((sum, row) => sum + row.metrics.plays, 0);
  const playRate = totalImpressions > 0 ? totalPlays / totalImpressions : 0;
  const ctaShown = published.reduce((sum, row) => sum + Math.round(row.metrics.plays * 0.7), 0);
  const ctaClicks = published.reduce(
    (sum, row) => sum + Math.round(row.metrics.plays * 0.7 * row.metrics.ctaClickRate),
    0,
  );
  const ctaRate = ctaShown > 0 ? ctaClicks / ctaShown : 0;
  const completion = published.length
    ? published.reduce((sum, row) => sum + row.metrics.completionRate, 0) / published.length
    : 0;
  const trend = published.length
    ? published.reduce((sum, row) => sum + row.metrics.playRateTrend, 0) / published.length
    : 0;

  const kpis = el("div", "fva-an-kpis");
  append(
    kpis,
    visualKpi("Reproduções totais", fmtCompact(totalPlays), trend, "play", [42, 48, 45, 58, 61, 67, 72]),
    visualKpi("Taxa de reprodução", fmtPct(playRate), 0.024, "trendingUp", [55, 57, 61, 60, 66, 69, 71]),
    visualKpi("Taxa de cliques na CTA", fmtPct(ctaRate), 0.008, "rocket", [4, 5, 5, 6, 7, 7, 8]),
    visualKpi("Conclusão média", fmtPct(completion), -0.006, "chart", [22, 21, 20, 19, 18, 18, 17]),
  );
  append(wrapper, kpis);

  const mainGrid = el("div", "fva-an-main-grid");
  append(
    mainGrid,
    renderTrend(totalPlays, playRate, periodDays),
    renderFunnel(totalImpressions, totalPlays, ctaShown, ctaClicks),
  );
  append(wrapper, mainGrid);

  const mixGrid = el("div", "fva-an-mix-grid");
  append(mixGrid, renderTrafficSources(totalPlays), renderDeviceMix(totalPlays));
  append(wrapper, mixGrid);

  append(wrapper, renderPerformanceTable(rows, store, navigate));
  return wrapper;
}

function visualKpi(
  label: string,
  value: string,
  trend: number,
  iconName: keyof typeof ICONS,
  points: number[],
): HTMLElement {
  const card = el("section", "fva-an-kpi");
  const head = el("div", "fva-an-kpi-head");
  append(head, el("p", "fva-an-kpi-label", label), icon(ICONS[iconName], "fva-an-kpi-icon"));
  const body = el("div", "fva-an-kpi-body");
  const valueWrap = el("div");
  append(valueWrap, el("p", "fva-an-kpi-value", value));
  append(valueWrap, el("span", trend >= 0 ? "fva-an-delta is-up" : "fva-an-delta is-down", `${fmtTrend(trend)} em relação ao período anterior`));
  append(body, valueWrap, sparkline(points, trend >= 0));
  append(card, head, body);
  return card;
}

function sparkline(points: number[], positive: boolean): SVGElement {
  const svg = svgEl("svg");
  svg.setAttribute("viewBox", "0 0 96 34");
  svg.classList.add("fva-an-spark");
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const d = points.map((point, index) => {
    const x = (index / Math.max(1, points.length - 1)) * 92 + 2;
    const y = 30 - ((point - min) / span) * 24;
    return `${index ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  const path = svgEl("path");
  path.setAttribute("d", d);
  path.setAttribute("class", positive ? "is-positive" : "is-negative");
  svg.appendChild(path);
  return svg;
}

function renderTrend(
  totalPlays: number,
  basePlayRate: number,
  periodDays: StatisticsPeriodDays,
): HTMLElement {
  const card = sectionCard("Tendência de desempenho", `Reproduções diárias e taxa de reprodução nos últimos ${periodDays} dias.`, "fva-an-trend");
  const legend = el("div", "fva-an-legend");
  append(legend, legendItem("Reproduções", "bar"), legendItem("Taxa de reprodução", "line"));
  card.appendChild(legend);

  const chart = el("div", "fva-an-chart");
  const svg = svgEl("svg");
  svg.setAttribute("viewBox", "0 0 760 270");
  svg.setAttribute("preserveAspectRatio", "none");
  const daily = dailySeries(totalPlays, basePlayRate, periodDays);
  const maxPlays = Math.max(...daily.map((day) => day.plays), 1);
  const left = 44;
  const right = 20;
  const top = 18;
  const bottom = 34;
  const plotW = 760 - left - right;
  const plotH = 270 - top - bottom;

  for (let i = 0; i <= 4; i++) {
    const y = top + (plotH / 4) * i;
    const line = svgEl("line");
    line.setAttribute("x1", String(left));
    line.setAttribute("x2", String(760 - right));
    line.setAttribute("y1", String(y));
    line.setAttribute("y2", String(y));
    line.setAttribute("class", "fva-an-grid-line");
    svg.appendChild(line);
  }

  const step = plotW / daily.length;
  daily.forEach((day, index) => {
    const height = (day.plays / maxPlays) * plotH;
    const rect = svgEl("rect");
    rect.setAttribute("x", String(left + index * step + step * 0.2));
    rect.setAttribute("y", String(top + plotH - height));
    rect.setAttribute("width", String(step * 0.6));
    rect.setAttribute("height", String(height));
    rect.setAttribute("rx", String(Math.min(3, step * 0.15)));
    rect.setAttribute("class", "fva-an-bar-rect");
    const title = svgEl("title");
    title.textContent = `${day.label}: ${fmtNumber(day.plays)} reproduções · ${fmtPct(day.rate)}`;
    rect.appendChild(title);
    svg.appendChild(rect);
  });

  const ratePath = daily.map((day, index) => {
    const x = left + index * step + step / 2;
    const normalized = Math.max(0, Math.min(1, (day.rate - 0.45) / 0.45));
    const y = top + plotH - normalized * plotH;
    return `${index ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  const path = svgEl("path");
  path.setAttribute("d", ratePath);
  path.setAttribute("class", "fva-an-rate-line");
  svg.appendChild(path);

  axisIndexes(daily.length).forEach((index) => {
    const text = svgEl("text");
    text.setAttribute("x", String(left + index * step + step / 2));
    text.setAttribute("y", "258");
    text.setAttribute("class", "fva-an-axis");
    text.textContent = daily[index].label;
    svg.appendChild(text);
  });
  chart.appendChild(svg);
  card.appendChild(chart);
  return card;
}

function renderFunnel(impressions: number, plays: number, shown: number, clicks: number): HTMLElement {
  const card = sectionCard("Funil de conversão", "Do carregamento do player ao clique na CTA.", "fva-an-funnel-card");
  const steps = [
    { label: "Impressões", value: impressions, color: "var(--fv-primary)" },
    { label: "Reproduções", value: plays, color: "var(--fv-primary-container)" },
    { label: "CTA exibida", value: shown, color: "#3f7f78" },
    { label: "CTA clicada", value: clicks, color: "#d97706" },
  ];
  const list = el("div", "fva-an-funnel");
  steps.forEach((step, index) => {
    const row = el("div", "fva-an-funnel-step");
    const head = el("div", "fva-an-funnel-head");
    const prior = index === 0 ? impressions : steps[index - 1].value;
    append(head, el("span", null, step.label), el("strong", null, fmtCompact(step.value)));
    const track = el("div", "fva-an-funnel-track");
    const fill = el("span") as HTMLElement;
    fill.style.width = `${Math.max(step.value ? 4 : 0, impressions ? (step.value / impressions) * 100 : 0)}%`;
    fill.style.background = step.color;
    track.appendChild(fill);
    append(row, head, track);
    if (index > 0) append(row, el("small", null, `${prior ? fmtPct(step.value / prior) : "0,0%"} da etapa anterior`));
    list.appendChild(row);
  });
  card.appendChild(list);
  return card;
}

function renderTrafficSources(totalPlays: number): HTMLElement {
  const card = sectionCard("Origens de tráfego", "Participação das reproduções atribuída a cada canal.", "fva-an-source-card");
  const sources = [
    ["Mídia paga", 0.34, "#00685f"],
    ["Pesquisa", 0.27, "#008378"],
    ["E-mail", 0.18, "#4f8f88"],
    ["Direto", 0.13, "#829b96"],
    ["Indicação", 0.08, "#d97706"],
  ] as const;
  const list = el("div", "fva-an-source-list");
  sources.forEach(([label, share, color]) => {
    const row = el("div", "fva-an-source-row");
    const head = el("div", "fva-an-source-head");
    const name = el("span");
    const swatch = el("i", "fva-an-swatch") as HTMLElement;
    swatch.style.background = color;
    append(name, swatch, document.createTextNode(label));
    append(head, name, el("strong", null, `${fmtCompact(Math.round(totalPlays * share))} · ${Math.round(share * 100)}%`));
    const track = el("div", "fva-an-source-track");
    const fill = el("span") as HTMLElement;
    fill.style.width = `${share * 100}%`;
    fill.style.background = color;
    track.appendChild(fill);
    append(row, head, track);
    list.appendChild(row);
  });
  card.appendChild(list);
  return card;
}

function renderDeviceMix(totalPlays: number): HTMLElement {
  const card = sectionCard("Dispositivos", "Onde os espectadores assistem às suas VSLs.", "fva-an-device-card");
  const body = el("div", "fva-an-device-body");
  const donut = el("div", "fva-an-donut");
  const center = el("div", "fva-an-donut-center");
  append(center, el("strong", null, "62%"), el("span", null, "Celular"));
  donut.appendChild(center);
  const legend = el("div", "fva-an-device-legend");
  [
    ["Celular", 0.62, "#00685f"],
    ["Computador", 0.31, "#7ba8a2"],
    ["Tablet", 0.07, "#d97706"],
  ].forEach(([label, share, color]) => {
    const row = el("div", "fva-an-device-row");
    const name = el("span");
    const swatch = el("i", "fva-an-swatch") as HTMLElement;
    swatch.style.background = String(color);
    append(name, swatch, document.createTextNode(String(label)));
    append(row, name, el("strong", null, `${fmtCompact(Math.round(totalPlays * Number(share)))} · ${Math.round(Number(share) * 100)}%`));
    legend.appendChild(row);
  });
  append(body, donut, legend);
  card.appendChild(body);
  return card;
}

function renderPerformanceTable(rows: AnalyticsRow[], store: VslStore, navigate: (path: string) => void): HTMLElement {
  const section = el("section", "fva-an-ranking");
  const head = el("div", "fva-an-section-head");
  const title = el("div");
  append(title, el("h3", null, "Desempenho das VSLs"), el("p", null, "Compare retenção e conversão entre as VSLs."));
  append(head, title, el("span", "fva-an-count", `${rows.length} VSL${rows.length === 1 ? "" : "s"}`));
  append(section, head);

  const tableWrap = el("div", "fva-table-scroll");
  const table = el("table", "fva-table fva-an-table");
  const thead = el("thead");
  const headerRow = el("tr");
  ["VSL", "Status", "Reproduções", "Taxa de reprodução", "Taxa de CTA", "Conclusão", ""].forEach((label) => append(headerRow, el("th", null, label)));
  thead.appendChild(headerRow);
  const tbody = el("tbody");
  rows.forEach(({ project, metrics }) => tbody.appendChild(renderRow(project, metrics, navigate)));
  append(table, thead, tbody);
  tableWrap.appendChild(table);
  section.appendChild(tableWrap);
  append(section, el("p", "fva-an-footnote", `${store.live ? "Dados reais" : "Dados de demonstração"} · Atualizado agora`));
  return section;
}

function renderRow(project: VslProject, metrics: Metrics, navigate: (path: string) => void): HTMLElement {
  const draft = project.status === "draft";
  const row = el("tr");
  row.addEventListener("click", () => navigate(`#/vsl/${project.id}/analytics`));
  const nameCell = el("td");
  const nameWrap = el("div", "fva-table-name");
  const thumb = el("div", draft ? "fva-table-thumb is-draft" : "fva-table-thumb");
  thumb.appendChild(icon(ICONS.play, "fva-nav-icon"));
  const text = el("div", "fva-table-name-text");
  append(text, el("p", null, project.name), el("span", null, `Atualizada em ${fmtDate(project.updatedAt)}`));
  append(nameWrap, thumb, text);
  nameCell.appendChild(nameWrap);
  append(row, nameCell);
  const statusCell = el("td");
  statusCell.appendChild(el("span", draft ? "fva-badge fva-badge-draft" : "fva-badge fva-badge-live", draft ? "Rascunho" : "Publicada"));
  append(row, statusCell);
  append(row, el("td", "num", draft ? "—" : fmtNumber(metrics.plays)));
  append(row, el("td", "num", draft ? "—" : fmtPct(metrics.playRate)));
  append(row, el("td", "num", draft ? "—" : fmtPct(metrics.ctaClickRate)));
  const completion = el("td", "num");
  if (draft) {
    append(completion, el("span", "fva-text-muted", "Pendente"));
  } else {
    const wrap = el("div", "fva-progress-mini");
    append(wrap, el("span", "pct", fmtPct(metrics.completionRate)));
    const bar = el("div", "fva-progress-bar");
    const fill = el("span") as HTMLElement;
    fill.style.width = `${metrics.completionRate * 100}%`;
    bar.appendChild(fill);
    append(wrap, bar);
    completion.appendChild(wrap);
  }
  append(row, completion);
  const action = el("td", "center");
  const button = el("button", "fva-icon-btn") as HTMLButtonElement;
  button.setAttribute("aria-label", `Ver estatísticas de ${project.name}`);
  button.appendChild(icon(ICONS.trendingUp, "fva-nav-icon"));
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    navigate(`#/vsl/${project.id}/analytics`);
  });
  action.appendChild(button);
  append(row, action);
  return row;
}

export function dailySeries(
  totalPlays: number,
  baseRate: number,
  periodDays: StatisticsPeriodDays,
): Array<{ label: string; plays: number; rate: number }> {
  const weights = Array.from({ length: periodDays }, (_, index) =>
    0.72 + (index / Math.max(1, periodDays - 1)) * 0.5 + Math.sin(index * 0.9) * 0.08,
  );
  const totalWeight = weights.reduce((sum, value) => sum + value, 0);
  const end = new Date();
  end.setUTCHours(0, 0, 0, 0);
  return weights.map((weight, index) => ({
    label: formatChartDate(new Date(end.getTime() - (periodDays - index - 1) * 86_400_000)),
    plays: Math.round((totalPlays * weight) / totalWeight),
    rate: Math.max(0.45, Math.min(0.9, baseRate + (index - (periodDays - 1) / 2) * (0.056 / periodDays) + Math.sin(index) * 0.012)),
  }));
}

export function scaleMetricsForPeriod(
  metrics: Metrics,
  periodDays: StatisticsPeriodDays,
): Metrics {
  const factor = periodDays / 14;
  return {
    ...metrics,
    impressions: Math.round(metrics.impressions * factor),
    plays: Math.round(metrics.plays * factor),
  };
}

function renderPeriodControl(selectedDays: StatisticsPeriodDays): HTMLElement {
  const wrap = el("label", "fva-an-period");
  append(wrap, el("span", null, "Período"));
  const select = el("select", "fva-select") as HTMLSelectElement;
  select.setAttribute("aria-label", "Período das estatísticas");
  for (const option of PERIOD_OPTIONS) {
    const node = el("option", null, option.label) as HTMLOptionElement;
    node.value = String(option.days);
    node.selected = option.days === selectedDays;
    select.appendChild(node);
  }
  select.addEventListener("change", () => {
    const days = Number(select.value) as StatisticsPeriodDays;
    savePeriod(days);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  });
  wrap.appendChild(select);
  return wrap;
}

function readPeriod(): StatisticsPeriodDays {
  try {
    const stored = Number(localStorage.getItem(PERIOD_STORAGE_KEY));
    if (PERIOD_OPTIONS.some((option) => option.days === stored)) {
      return stored as StatisticsPeriodDays;
    }
  } catch {
    // Partitioned or unavailable storage: use the product default.
  }
  return 14;
}

function savePeriod(days: StatisticsPeriodDays): void {
  try {
    localStorage.setItem(PERIOD_STORAGE_KEY, String(days));
  } catch {
    // The current render still updates even when storage is unavailable.
  }
}

function axisIndexes(length: number): number[] {
  const ticks = Math.min(6, length);
  return [...new Set(Array.from({ length: ticks }, (_, index) =>
    Math.round((index * (length - 1)) / Math.max(1, ticks - 1)),
  ))];
}

function formatChartDate(date: Date): string {
  return date.toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function sectionCard(title: string, subtitle: string, className: string): HTMLElement {
  const card = el("section", `fva-an-card ${className}`);
  append(card, el("h3", null, title), el("p", "fva-an-card-sub", subtitle));
  return card;
}

function legendItem(label: string, type: "bar" | "line"): HTMLElement {
  const item = el("span");
  append(item, el("i", `is-${type}`), document.createTextNode(label));
  return item;
}

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] {
  return document.createElementNS("http://www.w3.org/2000/svg", tag);
}

function renderEmpty(navigate: (path: string) => void): HTMLElement {
  const empty = el("div", "fva-empty");
  append(empty, el("h3", null, "Ainda não há dados de análise"), el("p", null, "Crie e publique uma VSL para começar a coletar dados de desempenho."));
  const cta = el("button", "fva-btn fva-btn-primary", "Nova VSL") as HTMLButtonElement;
  cta.addEventListener("click", () => navigate("#/new"));
  append(empty, cta);
  return empty;
}
